import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Divider, Grid, Stack, TextField, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Flex, colors } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AddIcon, ChevronDownIcon, DeleteIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { Popper } from "Components/_Shared/Popper";
import { type VehicleContractFormType, getVehicleContractFormSchema } from "Forms/Schemas/Assets/vehicleContractFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getListMovableAssetContractsQueryKey,
  useCreateVehicleAssetContract,
  useDeleteAssetContract,
  useGetVehicleAssetContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateVehicleAssetContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, ListAnalyzerPartnersToolType, type ProposedSolutionStep } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import React from "react";
import { type Control, type FieldPath, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";
import type { SubmitContractFormHandle } from "./ContractDrawer";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly onClose: () => void;
  readonly onPendingChange?: (pending: boolean) => void;
};

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: ${colors.backgroundSecondary};
  }
`;

const CenteredCheckbox: React.FC<{
  readonly name: FieldPath<VehicleContractFormType>;
  readonly control: Control<VehicleContractFormType>;
  readonly disabled: boolean;
  readonly ariaLabel: string;
}> = ({ name, control, disabled, ariaLabel }) => (
  <Flex justifyContent="center">
    <FormCheckbox name={name} control={control} disabled={disabled} ariaLabel={ariaLabel} />
  </Flex>
);

export const MovableContractVehicleForm = forwardRef<SubmitContractFormHandle, Props>(
  ({ assetItemId, assetContractId, onClose, onPendingChange }, ref) => {
    const queryClient = useQueryClient();
    const { t } = useResource();
    const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

    const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

    const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);
    const { data: vehicleContract } = useGetVehicleAssetContract(analysisId, versionId, assetItemId, assetContractId ?? "", {
      query: { enabled: !!assetContractId },
    });
    const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });

    const { mutateAsync: createAsync, isPending: isCreating } = useCreateVehicleAssetContract();
    const { mutateAsync: editAsync, isPending: isEditing } = useUpdateVehicleAssetContract();
    const { mutateAsync: deleteAsync, isPending: isDeleting } = useDeleteAssetContract();

    const isMutating = isCreating || isEditing || isDeleting;

    useEffect(() => {
      onPendingChange?.(isMutating);
    }, [isMutating, onPendingChange]);

    const defaultValues = useMemo(
      () => ({
        frequency: vehicleContract?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        newFrequency: vehicleContract?.proposedSolution?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        proposedSolutionStepType: vehicleContract?.proposedSolution?.step ?? "keep",
        partnerId: vehicleContract?.partnerId?.toString(),
        newPartnerId: vehicleContract?.proposedSolution?.partnerId?.toString() ?? undefined,
        contractAnniversaryDate: vehicleContract?.contractAnniversaryDate ?? undefined,
        insuranceAmount: vehicleContract?.insuranceAmount?.value.toString() ?? ("0" as any),
        newInsuranceAmount: vehicleContract?.proposedSolution?.insuranceAmount?.value.toString() ?? ("0" as any),
        note: vehicleContract?.note ?? undefined,
        compulsoryInsuranceCurrentAmount: vehicleContract?.vehicleCompulsoryInsurance.currentAmount?.value.toString() ?? ("0" as any),
        compulsoryInsuranceRecommendedAmount:
          vehicleContract?.vehicleCompulsoryInsurance.recommendedAmount?.value.toString() ?? ("0" as any),
        cascoInsuranceCurrentValue: vehicleContract?.vehicleCascoInsurance.hasCurrentContract,
        cascoInsuranceRecommendedValue: vehicleContract?.vehicleCascoInsurance.hasRecommendedContract,
        vehicleOtherInsuranceCurrentValue: vehicleContract?.vehicleOtherInsurance.hasCurrentContract,
        vehicleOtherInsuranceRecommendedValue: vehicleContract?.vehicleOtherInsurance.hasRecommendedContract,
        vehicleAssistanceInsuranceCurrentValue: vehicleContract?.vehicleAssistanceInsurance.hasCurrentContract,
        vehicleAssistanceInsuranceRecommendedValue: vehicleContract?.vehicleAssistanceInsurance.hasRecommendedContract,
        vehicleWindscreenInsuranceCurrentValue: vehicleContract?.vehicleWindscreenInsurance.hasCurrentContract,
        vehicleWindscreenInsuranceRecommendedValue: vehicleContract?.vehicleWindscreenInsurance.hasRecommendedContract,
        customAmountCoverages:
          vehicleContract?.customAmountCoverages?.map((ca) => ({
            name: ca.name,
            currentAmount: ca.currentAmount?.value.toString() ?? ("0" as any),
            recommendedAmount: ca.recommendedAmount?.value.toString() ?? ("0" as any),
          })) || ([] as any),
      }),
      [vehicleContract],
    );

    const methods = useForm<VehicleContractFormType>({
      resolver: yupResolver(getVehicleContractFormSchema(t)),
      mode: "onBlur",
      defaultValues,
    });

    const { control, handleSubmit, reset, setValue, watch } = methods;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "customAmountCoverages",
    });

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (!!assetContractId) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      delete: onDelete,
    }));

    const frequencies: Option[] = [
      { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
      { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
      { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
      { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
      { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
    ];

    const proposedSolutionStepTypes: Option[] = [
      { value: "keep", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Keep) },
      { value: "create_recommended", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.CreateRecommended) },
      { value: "modify", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Modify) },
    ];

    const insurancePartners: Option[] =
      insurancePartnersData?.map((partner) => ({ value: partner.id.toString(), name: partner.name })) || [];

    const buildAmount = (value: number | undefined, frequency: string) => ({
      value: value || 0,
      currencyCode: "CZK",
      frequency: frequency as AmountFrequency,
    });

    const buildBody = (data: VehicleContractFormType) => ({
      partnerId: Number(data.partnerId),
      contractAnniversaryDate: data.contractAnniversaryDate || undefined,
      insuranceAmount: buildAmount(data.insuranceAmount, data.frequency),
      proposedSolution: {
        step: data.proposedSolutionStepType as ProposedSolutionStep,
        partnerId: data.newPartnerId ? Number(data.newPartnerId) : undefined,
        insuranceAmount: buildAmount(data.newInsuranceAmount, data.newFrequency),
      },
      note: data.note || "",
      vehicleCompulsoryInsurance: {
        currentAmount: buildAmount(data.compulsoryInsuranceCurrentAmount, data.frequency),
        recommendedAmount: buildAmount(data.compulsoryInsuranceRecommendedAmount, data.frequency),
      },
      vehicleCascoInsurance: {
        hasCurrentContract: !!data.cascoInsuranceCurrentValue,
        hasRecommendedContract: !!data.cascoInsuranceRecommendedValue,
      },
      vehicleOtherInsurance: {
        hasCurrentContract: !!data.vehicleOtherInsuranceCurrentValue,
        hasRecommendedContract: !!data.vehicleOtherInsuranceRecommendedValue,
      },
      vehicleAssistanceInsurance: {
        hasCurrentContract: !!data.vehicleAssistanceInsuranceCurrentValue,
        hasRecommendedContract: !!data.vehicleAssistanceInsuranceRecommendedValue,
      },
      vehicleWindscreenInsurance: {
        hasCurrentContract: !!data.vehicleWindscreenInsuranceCurrentValue,
        hasRecommendedContract: !!data.vehicleWindscreenInsuranceRecommendedValue,
      },
      customAmountCoverages: data.customAmountCoverages.map((ca) => ({
        name: ca.name,
        currentAmount: buildAmount(ca.currentAmount, data.frequency),
        recommendedAmount: buildAmount(ca.recommendedAmount, data.frequency),
      })),
    });

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: getListMovableAssetContractsQueryKey(analysisId, versionId, assetItemId) });
    };

    const onCreate = async (data: VehicleContractFormType) => {
      try {
        await createAsync({ analysisId, versionId, itemId: assetItemId, data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractCreateSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onEdit = async (data: VehicleContractFormType) => {
      try {
        await editAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "", data: buildBody(data) });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractEditSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onDelete = async () => {
      try {
        await deleteAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "" });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractDeleteSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    useEffect(() => {
      reset(defaultValues);
    }, [reset, defaultValues]);

    const partnerId = watch("partnerId");
    const insuranceAmount = watch("insuranceAmount");
    const frequency = watch("frequency");
    const proposedSolutionStepType = watch("proposedSolutionStepType");

    return (
      <FormProvider {...methods}>
        <form>
          <Box>
            <Stack spacing={Spacing.XXL}>
              <Grid container spacing={Spacing.XL}>
                {expenseOptions && expenseOptions.length > 0 && (
                  <Grid size={6}>
                    <Popper
                      placement="bottom-start"
                      isOpen={expenseOptionsDropdownOpen}
                      onOpen={() => {
                        setExpenseOptionsDropdownOpen(true);
                      }}
                      btnEl={
                        <Button
                          variant="text"
                          disabled={readOnly}
                          sx={{ width: "fit-content" }}
                          endIcon={<ChevronDownIcon sx={{ color: "primary.main" }} />}
                        >
                          {t(Resources.AnalysisDetail.AssetsAndContracts.Form.LoadFromExistingExpenses)}
                        </Button>
                      }
                    >
                      <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                        {expenseOptions.map((option, index) => (
                          <StyledItem
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingX: Spacing.M,
                              paddingY: Spacing.S,
                              borderRadius: Radius.S,
                            }}
                            key={`${option.expenseType}-${index}`}
                            onClick={() => {
                              setValue("insuranceAmount", option.amount?.value.toString() as any);
                              if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                              setExpenseOptionsDropdownOpen(false);
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t(
                                  Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes[
                                    LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY[option.expenseType]
                                  ],
                                )}
                              </Typography>
                              <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                                {option.institution}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="secondary" fontWeight={500}>
                              {formatCurrency(option.amount?.value, option.amount?.currencyCode)}/
                              {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                            </Typography>
                          </StyledItem>
                        ))}
                      </Stack>
                    </Popper>
                  </Grid>
                )}
                <Grid size={8}>
                  <FormSelect
                    control={control}
                    name="partnerId"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
                    required
                    options={toSelectOptions(insurancePartners)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormDatePicker
                    control={control}
                    name="contractAnniversaryDate"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractAnniversaryDate)}
                    disabled={readOnly}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid size={6}>
                  <FormNumberField
                    control={control}
                    name="insuranceAmount"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                <Grid size={6}>
                  <FormSelect
                    control={control}
                    name="frequency"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                    options={toSelectOptions(frequencies)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="body1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceParameters)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="secondary" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.Parameter)}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentAmount)}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedAmount)}
                  </Typography>
                </Grid>
                {/* ------ */}
                <Grid size={6}>
                  <TextField
                    disabled
                    value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleCompulsoryInsuranceCoverage)}
                    fullWidth
                  />
                </Grid>
                <Grid size={3}>
                  <FormNumberField
                    control={control}
                    name="compulsoryInsuranceCurrentAmount"
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                <Grid size={3}>
                  <FormNumberField
                    control={control}
                    name="compulsoryInsuranceRecommendedAmount"
                    valueType="text"
                    scale={0}
                    min={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    disabled={readOnly}
                    fullWidth
                  />
                </Grid>
                {/* ------ */}
                <Grid size={6}>
                  <TextField disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleCascoInsuranceCoverage)} fullWidth />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="cascoInsuranceCurrentValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleCascoInsuranceCoverage),
                    })}
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="cascoInsuranceRecommendedValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleCascoInsuranceCoverage),
                    })}
                  />
                </Grid>
                {/* ------ */}
                <Grid size={6}>
                  <TextField
                    disabled
                    value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleWindscreenInsuranceCoverage)}
                    fullWidth
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleWindscreenInsuranceCurrentValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleWindscreenInsuranceCoverage),
                    })}
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleWindscreenInsuranceRecommendedValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleWindscreenInsuranceCoverage),
                    })}
                  />
                </Grid>
                {/* ------ */}
                <Grid size={6}>
                  <TextField disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleOtherInsuranceCoverage)} fullWidth />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleOtherInsuranceCurrentValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleOtherInsuranceCoverage),
                    })}
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleOtherInsuranceRecommendedValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleOtherInsuranceCoverage),
                    })}
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    disabled
                    value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleAssistanceInsuranceCoverage)}
                    fullWidth
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleAssistanceInsuranceCurrentValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleAssistanceInsuranceCoverage),
                    })}
                  />
                </Grid>
                <Grid size={3}>
                  <CenteredCheckbox
                    name="vehicleAssistanceInsuranceRecommendedValue"
                    control={control}
                    disabled={readOnly}
                    ariaLabel={t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedCoverageAriaLabel, {
                      coverage: t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleAssistanceInsuranceCoverage),
                    })}
                  />
                </Grid>
                {fields.map((field, index) => {
                  return (
                    <React.Fragment key={field.id}>
                      <Grid size={5}>
                        <FormTextField control={control} name={`customAmountCoverages.${index}.name`} disabled={readOnly} fullWidth />
                      </Grid>
                      <Grid size={3}>
                        <FormNumberField
                          control={control}
                          name={`customAmountCoverages.${index}.currentAmount`}
                          valueType="text"
                          scale={0}
                          min={0}
                          thousandsSeparator=" "
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={3}>
                        <FormNumberField
                          control={control}
                          name={`customAmountCoverages.${index}.recommendedAmount`}
                          valueType="text"
                          scale={0}
                          min={0}
                          thousandsSeparator=" "
                          unit={t(Resources.Common.CZKSuffix)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={1} display="flex" alignItems="center" justifyContent="center">
                        <Button
                          variant="text"
                          color="error"
                          disabled={readOnly}
                          sx={{ minWidth: "auto", padding: Spacing.S }}
                          onClick={() => remove(index)}
                        >
                          <DeleteIcon />
                        </Button>
                      </Grid>
                    </React.Fragment>
                  );
                })}
                <Grid size={12}>
                  <Button
                    variant="text"
                    disabled={readOnly}
                    sx={{ width: "fit-content", marginLeft: -Spacing.L }}
                    startIcon={<AddIcon />}
                    onClick={() =>
                      append({
                        name: "",
                        currentAmount: Number(0).toString() as any,
                        recommendedAmount: Number(0).toString() as any,
                      })
                    }
                  >
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.AddCoverage)}
                  </Button>
                </Grid>
              </Grid>

              <Divider />
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography fontWeight={600} variant="subtitle1">
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolutionSteps)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <FormSelect
                    control={control}
                    name="proposedSolutionStepType"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolution)}
                    options={toSelectOptions(proposedSolutionStepTypes)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={readOnly}
                    onChange={(value) => {
                      if (value === "modify") {
                        if (partnerId) setValue("newPartnerId", partnerId);
                        if (insuranceAmount) setValue("newInsuranceAmount", insuranceAmount);
                        if (frequency) setValue("newFrequency", frequency);
                      }
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
              {proposedSolutionStepType !== "keep" && (
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <FormSelect
                      control={control}
                      name="newPartnerId"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NewPartner)}
                      required
                      options={toSelectOptions(insurancePartners)}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="newInsuranceAmount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                      required
                      valueType="text"
                      scale={0}
                      min={0}
                      thousandsSeparator=" "
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormSelect
                      control={control}
                      name="newFrequency"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                      required
                      options={toSelectOptions(frequencies)}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      disabled={readOnly}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.Common.Note)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Stack>
          </Box>
        </form>
      </FormProvider>
    );
  },
);
